// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — reference category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "reference" — external references (xrefs) and in-place reference
// edit.
//
//   • REFEDIT  — in-place block / xref edit. Dispatches `space/enter-refedit`
//                + `space/exit-refedit` (editor.ts:1234-1247) and the working-set
//                add/remove pair (editor.ts:1252-1264). Activation is via the
//                RefeditToolbar; CLI typed-name routing is not yet wired in
//                App.tsx. status: "implemented" (the underlying literals work,
//                even if the CLI entry is pending).
//   • XATTACH  — attach an external DXF/DWG as an xref. Dispatches
//                `file/xattach` (editor.ts:1331). The current CLI handler
//                opens XrefManagerDialog rather than executing the
//                attachment directly (App.tsx:1322-1332).
//   • XBIND    — convert xref-bound named items into local copies.
//                Dispatches `file/xbind` (editor.ts:1341). CLI handler
//                opens the manager dialog (App.tsx:1333-1341).
//   • XCLIP    — apply / clear a clipping boundary on an xref. Dispatches
//                `file/xclip` (editor.ts:1365). CLI handler opens the
//                manager dialog (App.tsx:1349-1356).
//   • XUNLOAD / XRELOAD / XDETACH — manage xref load state and detachment.
//                Dispatch `file/xunload` (1346), `file/xreload` (1351),
//                `file/xdetach` (1360). CLI handlers open the manager
//                dialog as a single-window flow (App.tsx:1342-1357).
//
// Sources of truth: editor.ts:1234-1377 (REFEDIT + file/x* literals),
// App.tsx:1308-1357 (typed-name routing for XREF / XATTACH / XBIND / etc.),
// App.tsx:160-188 (recognised command list including X* + REFEDIT),
// messages.ts:1642 (RU command.refedit.label), :4150 (EN equivalent).
//
// COVERAGE_ALLOWLIST coordination: the file/x* literals (file/xattach,
// file/xbind, file/xunload, file/xreload, file/xdetach, file/xclip) are
// listed under group 6 of COVERAGE_ALLOWLIST as "descriptor migration
// pending". This file IS that migration: by enumerating each literal in
// the corresponding descriptor's `commandTypeLiterals` we shift them
// from "exempt from coverage" to "covered by REFEDIT-related descriptors".
// As part of this PR's roll-out the validator's forward-coverage check
// will start flagging the now-double-covered literals as duplicates;
// the resolution is to REMOVE the file/x* literals from the allowlist.
// That edit is intentionally NOT performed here (out-of-scope per Sub-A
// boundaries) but should land in the same merge: drop lines 82-87 of
// coverage-allowlist.ts. See repo-level coordination notes.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_GEOMETRY = "apps/web/src/app/state/geometryReducer.ts" as const;
const REDUCER_BLOCK_EDIT =
  "apps/web/src/app/state/blockEditReducer.ts" as const;

const REFEDIT: CommandDescriptor = {
  id: "REFEDIT",
  category: "reference",
  status: "implemented",
  // External-reference management / edit-session command — outside the AI
  // drafting safe contour (file/xref ops + REFEDIT session, absent from the
  // ТЗ §10.1 AI-callable matrix). Not AI-callable. [flagged for review]
  aiCallable: false,
  aliases: { en: ["REFEDIT"], ru: ["ССЫЛРЕД", "ВНЕДРЕД"] },
  commandTypeLiterals: [
    "space/enter-refedit",
    "space/exit-refedit",
    "space/refedit-add-to-working-set",
    "space/refedit-remove-from-working-set",
  ],
  parameters: [
    {
      name: "insertId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор INSERT (Block reference) или xref'а, для которого открывается сессия in-place edit. Снапшот block definition сохраняется автоматически на enter для возможного discard.",
        en: "INSERT (Block reference) or xref entity id to open the in-place edit session for. The block-definition snapshot is captured automatically on enter for a potential discard.",
      },
    },
    {
      name: "action",
      type: "enum",
      enumValues: ["save", "discard"],
      required: false,
      description: {
        ru: "Действие при выходе из сессии (REFCLOSE): save — фиксирует working-set обратно в block definition; discard — восстанавливает block из снапшота.",
        en: "Action on session exit (REFCLOSE): save — mirrors working-set back into the block definition; discard — restores the block from the snapshot.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Открывает сессию in-place редактирования блока или внешней ссылки (xref).",
      long: "Команда ССЫЛРЕД (REFEDIT) открывает сессию редактирования выбранного INSERT'а или xref'а 'на месте' — в синтетическом refedit-пространстве, где дочерние сущности блока становятся редактируемыми, а окружающие сущности host-пространства приглушаются (тонируются). Снапшот block definition фиксируется на enter для возможного discard. Геометрические мутации внутри сессии маршрутизируются на working-set; на REFCLOSE с action='save' изменения зеркалируются обратно в BlockDefinition (все INSERT'ы того же блока перерисовываются), при action='discard' исходный block восстанавливается из снапшота. Дополнительно работают REFSET-команды для добавления (space/refedit-add-to-working-set) и удаления (space/refedit-remove-from-working-set) сущностей в working-set. UI-выход сейчас через RefeditToolbar; CLI-вход типизированным именем планируется в более поздней фазе.",
      examples: ["REFEDIT", "ССЫЛРЕД", "ВНЕДРЕД"],
      seeAlso: ["BEDIT", "BSAVE", "BCLOSE"],
    },
    en: {
      short:
        "Opens an in-place edit session for a block or external reference (xref).",
      long: "The REFEDIT command opens an in-place edit session for the selected INSERT or xref — inside a synthetic refedit space where the block's child entities become editable while surrounding host-space entities render tinted. A block-definition snapshot is captured on enter for a potential discard. Geometry mutations inside the session route to the working-set; on REFCLOSE with action='save' the changes mirror back into the BlockDefinition (all INSERTs of the same block re-render), with action='discard' the original block is restored from the snapshot. Companion REFSET commands add (space/refedit-add-to-working-set) or remove (space/refedit-remove-from-working-set) entities from the working-set. UI exit is currently via the RefeditToolbar; typed-name CLI entry is scheduled for a later phase.",
      examples: ["REFEDIT"],
      seeAlso: ["BEDIT", "BSAVE", "BCLOSE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.refedit.label",
    labelKeyEn: "command.refedit.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "RE",
  },
  reducerLocations: [
    { commandTypeLiteral: "space/enter-refedit", file: REDUCER_BLOCK_EDIT },
    { commandTypeLiteral: "space/exit-refedit", file: REDUCER_BLOCK_EDIT },
    {
      commandTypeLiteral: "space/refedit-add-to-working-set",
      file: REDUCER_BLOCK_EDIT,
    },
    {
      commandTypeLiteral: "space/refedit-remove-from-working-set",
      file: REDUCER_BLOCK_EDIT,
    },
  ],
};

const XATTACH: CommandDescriptor = {
  id: "XATTACH",
  category: "reference",
  status: "implemented",
  // External-reference management / edit-session command — outside the AI
  // drafting safe contour (file/xref ops + REFEDIT session, absent from the
  // ТЗ §10.1 AI-callable matrix). Not AI-callable. [flagged for review]
  aiCallable: false,
  aliases: { en: ["XATTACH", "XA"], ru: ["ССВСТАВИТЬ", "ВНПРИС"] },
  commandTypeLiterals: ["file/xattach"],
  parameters: [
    {
      name: "xrefName",
      type: "string",
      required: true,
      description: {
        ru: "Имя внешней ссылки (имя block record'а в блок-таблице). Должно быть уникальным.",
        en: "External reference name (block-record name in the block table). Must be unique.",
      },
    },
    {
      name: "xrefPath",
      type: "string",
      required: true,
      description: {
        ru: "Путь к внешнему файлу (DXF/DWG). Используется для повторной загрузки и UI отображения.",
        en: "Path to the external file (DXF/DWG). Used for reloads and UI display.",
      },
    },
    {
      name: "xrefEntities",
      type: "object",
      required: false,
      description: {
        ru: "Pre-parsed массив сущностей xref'а. Браузер не может читать произвольные файлы синхронно — file picker и парсинг выполняются в App.tsx, затем результат передаётся в command. По умолчанию пустой массив (пустой xref).",
        en: "Pre-parsed array of xref entities. The browser cannot read arbitrary files synchronously — the file picker and parser run in App.tsx, then the entities are passed in. Defaults to an empty array.",
      },
    },
    {
      name: "attachmentType",
      type: "enum",
      enumValues: ["attach", "overlay"],
      required: true,
      description: {
        ru: "Тип прикрепления: attach (вложенные xref'ы видны при последующих xref'ах этого файла) или overlay (вложенные xref'ы НЕ видны вышестоящим).",
        en: "Attachment type: attach (nested xrefs are visible to subsequent xrefs of this file) or overlay (nested xrefs are NOT visible to higher xrefs).",
      },
    },
    {
      name: "insertionPoint",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка вставки xref'а (x, y) в координатах целевого пространства.",
        en: "Insertion point (x, y) of the xref in target-space coordinates.",
      },
    },
    {
      name: "insertSpaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в которое вставляется xref.",
        en: "Identifier of the space the xref is inserted into.",
      },
    },
  ],
  help: {
    ru: {
      short: "Прикрепляет внешний DXF/DWG файл как внешнюю ссылку (xref).",
      long: "Команда ССВСТАВИТЬ (XATTACH) прикрепляет внешний DXF / DWG файл к текущему чертежу как внешнюю ссылку (xref). Поскольку браузер не может читать произвольные файлы синхронно, выбор файла выполняется через стандартный file picker в App.tsx, парсинг — там же, и затем готовые сущности передаются в команду через xrefEntities. Поддерживаются два режима: 'attach' (вложенные xref'ы видны вышестоящим) и 'overlay' (вложенные не видны). Текущий CLI-обработчик открывает централизованный XrefManagerDialog (XREF / -XREF), внутри которого пользователь жмёт Attach для запуска picker'а — это унифицирует UX. Прямая command-line форма с параметрами ожидается в следующей фазе. После attach создаётся специальный block record с именем xrefName, помеченный как xref; экземпляр становится обычным INSERT'ом, который можно перемещать, копировать и т.д.",
      examples: ["XATTACH", "XA", "ССВСТАВИТЬ", "ВНПРИС"],
      seeAlso: ["XBIND", "XCLIP", "XUNLOAD", "XRELOAD", "XDETACH"],
    },
    en: {
      short:
        "Attaches an external DXF/DWG file as an external reference (xref).",
      long: "The XATTACH command attaches an external DXF / DWG file to the current drawing as an external reference (xref). Because the browser cannot read arbitrary files synchronously, file selection happens via the standard picker in App.tsx, parsing runs there too, and the prepared entities are passed in via xrefEntities. Two attachment types: 'attach' (nested xrefs are visible upstream) and 'overlay' (nested are NOT visible). The current CLI handler opens the centralised XrefManagerDialog (XREF / -XREF) where the user clicks Attach to launch the picker — this unifies UX. A direct parameter-form command line is scheduled for a later phase. On attach a special block record is created with name xrefName, flagged as xref; the instance becomes an ordinary INSERT that can be moved, copied, etc.",
      examples: ["XATTACH", "XA"],
      seeAlso: ["XBIND", "XCLIP", "XUNLOAD", "XRELOAD", "XDETACH"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xattach.label",
    labelKeyEn: "command.xattach.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "XA",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/xattach", file: REDUCER_GEOMETRY },
  ],
};

const XBIND: CommandDescriptor = {
  id: "XBIND",
  category: "reference",
  status: "implemented",
  // External-reference management / edit-session command — outside the AI
  // drafting safe contour (file/xref ops + REFEDIT session, absent from the
  // ТЗ §10.1 AI-callable matrix). Not AI-callable. [flagged for review]
  aiCallable: false,
  aliases: { en: ["XBIND", "XB"], ru: ["ССВНЕДРИТЬ", "ВНВНЕДР"] },
  commandTypeLiterals: ["file/xbind"],
  parameters: [
    {
      name: "xrefName",
      type: "string",
      required: true,
      description: {
        ru: "Имя внешней ссылки, которую нужно превратить в обычный (внедрённый) блок. После XBIND xref становится локальным block record'ом, неотличимым от созданных через BLOCK.",
        en: "Name of the external reference to convert into a regular (bound) block. After XBIND the xref becomes a local block record indistinguishable from those created via BLOCK.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Внедряет внешнюю ссылку (xref) в текущий чертёж — превращает её в обычный блок.",
      long: "Команда ССВНЕДРИТЬ (XBIND) превращает внешнюю ссылку (xref) в обычный (локальный) блок текущего чертежа. После выполнения связь с внешним файлом разрывается; именованные объекты xref'а (слои, типы линий, текстовые стили, размерные стили) копируются в чертёж с префиксом '<xref>$0$' (либо $1$, $2$, … при коллизии имён). Это удобно когда чертёж нужно отдать без сопроводительных файлов. Текущий CLI-обработчик открывает XrefManagerDialog для выбора xref'а; прямой command-line вызов с параметром xrefName появится в следующей фазе.",
      examples: ["XBIND", "XB", "ССВНЕДРИТЬ"],
      seeAlso: ["XATTACH", "XDETACH"],
    },
    en: {
      short:
        "Binds an external reference (xref) into the current drawing — converts it into a regular block.",
      long: "The XBIND command turns an external reference (xref) into a regular (local) block of the current drawing. After execution the link to the external file is severed; named objects from the xref (layers, line types, text styles, dimension styles) are copied into the drawing with a '<xref>$0$' prefix (or $1$, $2$, … on name collisions). Useful when shipping the drawing standalone. The current CLI handler opens the XrefManagerDialog for xref selection; a direct command-line call with the xrefName parameter is scheduled for a later phase.",
      examples: ["XBIND", "XB"],
      seeAlso: ["XATTACH", "XDETACH"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xbind.label",
    labelKeyEn: "command.xbind.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "XB",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/xbind", file: REDUCER_GEOMETRY },
  ],
};

const XCLIP: CommandDescriptor = {
  id: "XCLIP",
  category: "reference",
  status: "implemented",
  // External-reference management / edit-session command — outside the AI
  // drafting safe contour (file/xref ops + REFEDIT session, absent from the
  // ТЗ §10.1 AI-callable matrix). Not AI-callable. [flagged for review]
  aiCallable: false,
  aliases: { en: ["XCLIP", "CLIP"], ru: ["ССКОНТУР", "ВНОБРЕЗ"] },
  commandTypeLiterals: ["file/xclip"],
  parameters: [
    {
      name: "xrefName",
      type: "string",
      required: true,
      description: {
        ru: "Имя внешней ссылки, к которой применяется граница обрезки. Граница хранится на уровне block record'а xref'а.",
        en: "Name of the external reference the clipping boundary applies to. The boundary is stored on the xref's block record.",
      },
    },
    {
      name: "boundary",
      type: "object",
      required: true,
      description: {
        ru: "Описание границы обрезки: { type: 'rectangle' | 'polygon', vertices: [{x,y}, ...] } с минимум 2 вершинами, либо null для удаления существующей границы (XCLIP DELETE в AutoCAD).",
        en: "Clipping boundary descriptor: { type: 'rectangle' | 'polygon', vertices: [{x,y}, ...] } with at least 2 vertices, or null to clear an existing boundary (XCLIP DELETE in AutoCAD).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Применяет или удаляет границу обрезки (clipping boundary) для внешней ссылки.",
      long: "Команда ССКОНТУР (XCLIP) задаёт границу обрезки для внешней ссылки — отображается только содержимое xref'а внутри указанного контура (прямоугольник или произвольный многоугольник). Это полезно когда нужно показать только часть xref'а (например, только нужный фрагмент карты). Передача boundary=null удаляет ранее установленную границу (соответствует XCLIP DELETE в AutoCAD). Граница хранится на уровне block record'а xref'а, поэтому затрагивает все INSERT'ы этой ссылки. Текущий CLI-обработчик открывает XrefManagerDialog для выбора xref'а; интерактивный pick-flow границы запланирован на следующую фазу.",
      examples: ["XCLIP", "ССКОНТУР", "ВНОБРЕЗ"],
      seeAlso: ["XATTACH", "XBIND"],
    },
    en: {
      short: "Applies or removes a clipping boundary on an external reference.",
      long: "The XCLIP command sets a clipping boundary on an external reference — only the xref content inside the specified contour (rectangle or arbitrary polygon) is rendered. Useful when only part of an xref should be visible (e.g. a single map fragment). Passing boundary=null clears a previously set boundary (matches XCLIP DELETE in AutoCAD). The boundary is stored on the xref's block record, so it applies to all INSERTs of that reference. The current CLI handler opens the XrefManagerDialog for xref selection; an interactive boundary pick-flow is scheduled for a later phase.",
      examples: ["XCLIP"],
      seeAlso: ["XATTACH", "XBIND"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xclip.label",
    labelKeyEn: "command.xclip.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "XC",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/xclip", file: REDUCER_GEOMETRY },
  ],
};

const XUNLOAD: CommandDescriptor = {
  id: "XUNLOAD",
  category: "reference",
  status: "implemented",
  // External-reference management / edit-session command — outside the AI
  // drafting safe contour (file/xref ops + REFEDIT session, absent from the
  // ТЗ §10.1 AI-callable matrix). Not AI-callable. [flagged for review]
  aiCallable: false,
  aliases: { en: ["XUNLOAD"], ru: ["ССВЫГРУЗИТЬ", "ВНВЫГР"] },
  commandTypeLiterals: ["file/xunload"],
  parameters: [
    {
      name: "xrefName",
      type: "string",
      required: true,
      description: {
        ru: "Имя внешней ссылки для выгрузки. Запись о xref'е сохраняется (можно перезагрузить через XRELOAD), но содержимое в памяти освобождается.",
        en: "Name of the external reference to unload. The xref record is preserved (XRELOAD can re-load), but the in-memory content is released.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Выгружает содержимое внешней ссылки из памяти, сохраняя запись о xref'е.",
      long: "Команда ССВЫГРУЗИТЬ (XUNLOAD) освобождает память, занимаемую содержимым внешней ссылки — сущности xref'а удаляются из активной модели, но запись о xref'е (имя, путь, attachment type) остаётся в чертеже. Выгруженный xref отображается как пустой контейнер. Команда полезна для оптимизации производительности когда крупные xref'ы временно не нужны. Восстановить содержимое можно через XRELOAD. В отличие от XDETACH, XUNLOAD не разрывает связь — запись о xref'е сохраняется, и при следующем открытии файла xref может быть автоматически перезагружен. Текущий CLI-обработчик открывает XrefManagerDialog для выбора xref'а.",
      examples: ["XUNLOAD", "ССВЫГРУЗИТЬ", "ВНВЫГР"],
      seeAlso: ["XRELOAD", "XDETACH", "XATTACH"],
    },
    en: {
      short:
        "Unloads an external reference's content from memory while preserving the xref record.",
      long: "The XUNLOAD command releases the memory occupied by an external reference's content — xref entities are removed from the active model, but the xref record (name, path, attachment type) stays in the drawing. The unloaded xref renders as an empty container. Useful for performance tuning when large xrefs are temporarily not needed. Content can be restored via XRELOAD. Unlike XDETACH, XUNLOAD does NOT break the link — the xref record is preserved and on next file open the xref can be auto-loaded. The current CLI handler opens the XrefManagerDialog for selection.",
      examples: ["XUNLOAD"],
      seeAlso: ["XRELOAD", "XDETACH", "XATTACH"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xunload.label",
    labelKeyEn: "command.xunload.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "XU",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/xunload", file: REDUCER_GEOMETRY },
  ],
};

const XRELOAD: CommandDescriptor = {
  id: "XRELOAD",
  category: "reference",
  status: "implemented",
  // External-reference management / edit-session command — outside the AI
  // drafting safe contour (file/xref ops + REFEDIT session, absent from the
  // ТЗ §10.1 AI-callable matrix). Not AI-callable. [flagged for review]
  aiCallable: false,
  aliases: { en: ["XRELOAD"], ru: ["ССПЕРЕЗАГР", "ВНПЕРЕЗ"] },
  commandTypeLiterals: ["file/xreload"],
  parameters: [
    {
      name: "xrefName",
      type: "string",
      required: true,
      description: {
        ru: "Имя внешней ссылки для перезагрузки. Если xref выгружен (XUNLOAD), его содержимое восстанавливается; если ссылка указывает на изменённый файл, обновлённое содержимое подгружается.",
        en: "Name of the external reference to reload. If the xref is unloaded (XUNLOAD) its content is restored; if the link points to an updated file the new content is pulled.",
      },
    },
    {
      name: "newEntities",
      type: "object",
      required: false,
      description: {
        ru: "Опциональный массив свежих pre-parsed сущностей. Передаётся когда внешний файл изменился и был перепарсен в App.tsx.",
        en: "Optional fresh pre-parsed entities array. Passed when the external file changed and was re-parsed in App.tsx.",
      },
    },
    {
      name: "newPath",
      type: "string",
      required: false,
      description: {
        ru: "Опциональный новый путь к файлу — когда пользователь хочет перенаправить xref на другой файл.",
        en: "Optional new file path — when the user re-points the xref to a different file.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Перезагружает внешнюю ссылку с диска (после XUNLOAD или изменений в исходном файле).",
      long: "Команда ССПЕРЕЗАГР (XRELOAD) повторно загружает содержимое внешней ссылки. Используется в двух сценариях: (1) после XUNLOAD — восстановить содержимое выгруженного xref'а; (2) после изменения исходного файла — подтянуть свежую версию (что-то изменилось в источнике, нужно обновить отображение). Поскольку браузер не может синхронно читать файлы, повторный парсинг выполняется в App.tsx, и готовые сущности передаются через newEntities. Опциональный newPath позволяет одновременно перенаправить ссылку на другой файл (например, когда исходник перемещён).",
      examples: ["XRELOAD", "ССПЕРЕЗАГР"],
      seeAlso: ["XUNLOAD", "XATTACH", "XDETACH"],
    },
    en: {
      short:
        "Reloads an external reference from disk (after XUNLOAD or external file changes).",
      long: "The XRELOAD command re-loads an external reference's content. Used in two scenarios: (1) after XUNLOAD — restore the unloaded xref's content; (2) after external file changes — pull the fresh version (something changed at the source, the display needs refreshing). Because the browser cannot synchronously read files, re-parsing runs in App.tsx and the prepared entities pass via newEntities. The optional newPath lets you simultaneously re-point the reference to a different file (e.g. when the source file moved).",
      examples: ["XRELOAD"],
      seeAlso: ["XUNLOAD", "XATTACH", "XDETACH"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xreload.label",
    labelKeyEn: "command.xreload.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "XR",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/xreload", file: REDUCER_GEOMETRY },
  ],
};

const XDETACH: CommandDescriptor = {
  id: "XDETACH",
  category: "reference",
  status: "implemented",
  // External-reference management / edit-session command — outside the AI
  // drafting safe contour (file/xref ops + REFEDIT session, absent from the
  // ТЗ §10.1 AI-callable matrix). Not AI-callable. [flagged for review]
  aiCallable: false,
  aliases: { en: ["XDETACH"], ru: ["ССОТСОЕД", "ВНОТКР"] },
  commandTypeLiterals: ["file/xdetach"],
  parameters: [
    {
      name: "xrefName",
      type: "string",
      required: true,
      description: {
        ru: "Имя внешней ссылки для полного отсоединения. После XDETACH запись о xref'е удаляется из чертежа; восстановить можно только повторным XATTACH.",
        en: "Name of the external reference to fully detach. After XDETACH the xref record is removed from the drawing; only re-attaching via XATTACH restores it.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Полностью удаляет внешнюю ссылку из чертежа (необратимо без XATTACH).",
      long: "Команда ССОТСОЕД (XDETACH) полностью отсоединяет внешнюю ссылку — удаляет и содержимое из памяти, и саму запись о xref'е из чертежа. В отличие от XUNLOAD (где запись сохраняется и можно перезагрузить через XRELOAD), XDETACH необратим: для восстановления нужен повторный XATTACH с тем же файлом. Все INSERT'ы выгружаемого xref'а удаляются из всех пространств. Команда не может выгрузить xref если на него есть ссылки в активном REFEDIT (нужно сначала закрыть сессию). Текущий CLI-обработчик открывает XrefManagerDialog для выбора xref'а с возможностью detach.",
      examples: ["XDETACH", "ССОТСОЕД"],
      seeAlso: ["XATTACH", "XUNLOAD", "XBIND"],
    },
    en: {
      short:
        "Fully removes an external reference from the drawing (irreversible without XATTACH).",
      long: "The XDETACH command fully detaches an external reference — removing both its in-memory content and the xref record itself from the drawing. Unlike XUNLOAD (where the record persists and XRELOAD restores content), XDETACH is irreversible: re-attaching requires running XATTACH against the same file. All INSERTs of the detached xref are removed from every space. The command cannot detach an xref while there is an active REFEDIT session referencing it (close the session first). The current CLI handler opens the XrefManagerDialog for selection with a detach option.",
      examples: ["XDETACH"],
      seeAlso: ["XATTACH", "XUNLOAD", "XBIND"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xdetach.label",
    labelKeyEn: "command.xdetach.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "XD",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/xdetach", file: REDUCER_GEOMETRY },
  ],
};

// D5 (App.tsx router→registry unification) — XREF and XOPEN were dispatched
// via literal `upperStart === "..."` comparisons in App.tsx's
// submitCommandText before these descriptors landed. Both route to the same
// XrefManagerDialog as XATTACH/XBIND/XUNLOAD/XRELOAD/XDETACH/XCLIP — the
// dialog itself resolves the concrete per-xref action (attach / bind /
// unload / reload / detach / clip), so neither verb carries its own
// commandTypeLiteral.
const XREF: CommandDescriptor = {
  id: "XREF",
  category: "reference",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["XREF", "-XREF", "XR"], ru: ["ВНССЫЛ"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short: "Открывает диспетчер внешних ссылок (External References).",
      long: "Команда ВНССЫЛ (XREF) открывает XrefManagerDialog — список всех присоединённых внешних ссылок (DWG/DXF xref'ов) чертежа со статусом (загружена / выгружена / не найдена) и действиями attach / bind / unload / reload / detach прямо из диалога. Форма -XREF зарезервирована под безUIшный вариант (сегодня ведёт себя как XREF).",
      examples: ["XREF", "-XREF", "XR", "ВНССЫЛ"],
      seeAlso: ["XATTACH", "XBIND", "XCLIP", "XDETACH"],
    },
    en: {
      short: "Opens the External References manager dialog.",
      long: "The XREF command opens XrefManagerDialog — a list of every attached external reference (DWG/DXF xref) in the drawing with its status (loaded / unloaded / not found) and attach / bind / unload / reload / detach actions available directly from the dialog. The -XREF form is reserved for a future no-UI variant (today it behaves like XREF).",
      examples: ["XREF", "XR"],
      seeAlso: ["XATTACH", "XBIND", "XCLIP", "XDETACH"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xref.label",
    labelKeyEn: "command.xref.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "XR",
  },
  reducerLocations: [],
};

const XOPEN: CommandDescriptor = {
  id: "XOPEN",
  category: "reference",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["XOPEN"], ru: ["ВНОТКРЫТЬ"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short: "Открывает выбранную внешнюю ссылку в диспетчере ссылок.",
      long: "Команда ВНОТКРЫТЬ (XOPEN) в AutoCAD открывает выбранный xref-файл в новом окне для прямого редактирования. В текущей browser-реализации отдельного окна редактирования xref-исходника нет, поэтому команда открывает тот же XrefManagerDialog, что и XREF, откуда пользователь может выбрать ссылку и её действия.",
      examples: ["XOPEN", "ВНОТКРЫТЬ"],
      seeAlso: ["XREF", "XATTACH", "XBIND"],
    },
    en: {
      short: "Opens the selected external reference in the manager dialog.",
      long: "In AutoCAD, XOPEN opens the selected xref file in a new window for direct editing. The current browser implementation has no separate xref-source editing window, so the command opens the same XrefManagerDialog as XREF, from which the user can pick a reference and its actions.",
      examples: ["XOPEN"],
      seeAlso: ["XREF", "XATTACH", "XBIND"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.xopen.label",
    labelKeyEn: "command.xopen.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

export const REFERENCE_DESCRIPTORS: readonly CommandDescriptor[] = [
  REFEDIT,
  XATTACH,
  XBIND,
  XCLIP,
  XDETACH,
  XRELOAD,
  XUNLOAD,
  XREF,
  XOPEN,
];
